import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getDhOrders, getDhStats, getDhUsage, getDhActive, sendMockDhOrder } from '../../services/api';
import {
  Truck, Package, AlertTriangle, CheckCircle, Clock, XCircle,
  RefreshCw, ChevronLeft, ChevronRight, Zap, Send, Radio,
  Store, ArrowLeft, ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const TEST_EMAIL = 'tienda2@test.com';

const STATUS_LABELS = {
  received: { label: 'Recibido', color: 'bg-blue-100 text-blue-700' },
  sent_to_pos: { label: 'Enviado a POS', color: 'bg-indigo-100 text-indigo-700' },
  ack_by_pos: { label: 'Confirmado', color: 'bg-purple-100 text-purple-700' },
  preparing: { label: 'Preparando', color: 'bg-yellow-100 text-yellow-700' },
  ready: { label: 'Listo', color: 'bg-emerald-100 text-emerald-700' },
  picked_up: { label: 'Recogido', color: 'bg-teal-100 text-teal-700' },
  in_transit: { label: 'En transito', color: 'bg-cyan-100 text-cyan-700' },
  delivered: { label: 'Entregado', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Fallido', color: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelado', color: 'bg-slate-100 text-slate-600' },
};

const CHANNEL_LABELS = {
  mock: 'Mock', marketplace: 'Marketplace', menu_digital: 'Menu Digital',
  uber_eats: 'Uber Eats', rappi: 'Rappi', didi_food: 'DiDi Food',
};

const DeliveryHub = () => {
  const { user } = useAuth();
  const isTestAccount = user?.email === TEST_EMAIL;

  // Branch selection state
  const [dhInfo, setDhInfo] = useState(null);
  const [loadingDhInfo, setLoadingDhInfo] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Branch data state
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [branchUsage, setBranchUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [sendingMock, setSendingMock] = useState(false);

  // Load DH active info (which branches have addon)
  useEffect(() => {
    setLoadingDhInfo(true);
    getDhActive()
      .then(res => {
        setDhInfo(res.data);
        // Auto-select if only 1 branch
        if (res.data?.branches?.length === 1) {
          setSelectedBranch(res.data.branches[0]);
        }
      })
      .catch(() => setDhInfo({ active: false, branches: [] }))
      .finally(() => setLoadingDhInfo(false));
  }, []);

  // Load branch-specific data
  const loadBranchData = useCallback(async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      const params = { page, limit: 20, branch_id: selectedBranch.branch_id };
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channel = channelFilter;

      const [ordersRes, statsRes, usageRes] = await Promise.all([
        getDhOrders(params),
        getDhStats(),
        getDhUsage({ branch_id: selectedBranch.branch_id }),
      ]);
      setOrders(ordersRes.data?.orders || []);
      setPagination(ordersRes.data?.pagination);
      setStats(statsRes.data);
      // Usage now returns branches array — find the selected branch
      const branches = usageRes.data?.branches || [];
      const thisUsage = branches.find(b => b.branch_id === selectedBranch.branch_id) || null;
      setBranchUsage(thisUsage);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, page, statusFilter, channelFilter]);

  useEffect(() => {
    if (selectedBranch) loadBranchData();
  }, [loadBranchData, selectedBranch]);

  const handleSendMock = async () => {
    if (!selectedBranch) return;
    setSendingMock(true);
    try {
      await sendMockDhOrder({ branch_id: selectedBranch.branch_id });
      toast.success('Pedido mock creado');
      loadBranchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear mock');
    } finally {
      setSendingMock(false);
    }
  };

  const handleBackToBranches = () => {
    setSelectedBranch(null);
    setOrders([]);
    setStats(null);
    setBranchUsage(null);
    setPage(1);
    setStatusFilter('');
    setChannelFilter('');
  };

  const usagePercent = branchUsage?.usage_percent ?? 0;
  const usageBarColor = usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-yellow-500' : 'bg-emerald-500';

  // Loading initial info
  if (loadingDhInfo) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // No DH addon active — upsell CTA
  if (!dhInfo?.active) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-12">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Radio className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Delivery Hub</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Centraliza los pedidos de Uber Eats, Rappi, DiDi Food y mas plataformas directamente en tu POS.
              Recibe, gestiona y da seguimiento desde un solo lugar.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-slate-50 rounded-xl p-4">
                <Package className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Pedidos unificados</p>
                <p className="text-xs text-slate-400 mt-1">Todas las plataformas en un solo lugar</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <Truck className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Seguimiento en vivo</p>
                <p className="text-xs text-slate-400 mt-1">Estados sincronizados con tu POS</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <CheckCircle className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Sin errores</p>
                <p className="text-xs text-slate-400 mt-1">Elimina la captura manual</p>
              </div>
            </div>
            <a
              href="/dashboard/comprar-addons"
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <ShoppingBag className="w-5 h-5" />
              Ver planes disponibles
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Has DH but no branch selected — show branch cards
  if (!selectedBranch) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Radio className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Delivery Hub</h1>
              <p className="text-sm text-slate-500">Selecciona una sucursal para ver sus pedidos</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {dhInfo.branches.map((branch) => (
              <button
                key={branch.branch_id}
                onClick={() => setSelectedBranch(branch)}
                className="bg-white rounded-2xl border-2 border-slate-200 p-7 text-left hover:border-indigo-400 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-100 transition-colors">
                    <Store className="w-7 h-7 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-slate-900 truncate">{branch.branch_name}</h3>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 mt-1.5 inline-block">
                      {branch.addon_name}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <span className="text-sm text-slate-400">Ver pedidos y uso</span>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Branch selected — show full dashboard
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {dhInfo.branches.length > 1 && (
              <button
                onClick={handleBackToBranches}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Truck className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Delivery Hub</h1>
              <p className="text-sm text-slate-500">{selectedBranch.branch_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isTestAccount && (
              <button
                onClick={handleSendMock}
                disabled={sendingMock}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                <Zap className="w-4 h-4" />
                {sendingMock ? 'Enviando...' : 'Mock Order'}
              </button>
            )}
            <button
              onClick={loadBranchData}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Usage Bar + Stats Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Usage Card */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Uso mensual</h3>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                {branchUsage?.addon_name || selectedBranch.addon_name}
              </span>
            </div>

            {branchUsage?.limit ? (
              <>
                <div className="flex items-end justify-between mb-2">
                  <span className="text-2xl font-bold text-slate-900">
                    {(branchUsage.orders_this_month || 0).toLocaleString('es-MX')}
                  </span>
                  <span className="text-sm text-slate-500">
                    de {branchUsage.limit.toLocaleString('es-MX')} pedidos
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${usageBarColor}`}
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>{usagePercent}% usado</span>
                  {branchUsage.overage_count > 0 && (
                    <span className="text-red-600 font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {branchUsage.overage_count} excedentes (${branchUsage.overage_cost_total} MXN)
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-slate-900">
                  {(branchUsage?.orders_this_month || 0).toLocaleString('es-MX')}
                </span>
                <span className="text-sm text-emerald-600 font-medium">Ilimitado</span>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Resumen</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Total pedidos</span>
                <span className="text-sm font-bold text-slate-900">{stats?.total_orders || 0}</span>
              </div>
              {stats?.by_channel && Object.entries(stats.by_channel).map(([ch, count]) => (
                <div key={ch} className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">{CHANNEL_LABELS[ch] || ch}</span>
                  <span className="text-sm font-medium text-slate-700">{count}</span>
                </div>
              ))}
              {stats?.outbox?.pending > 0 && (
                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                  <span className="text-sm text-yellow-600">Outbox pendiente</span>
                  <span className="text-sm font-medium text-yellow-700">{stats.outbox.pending}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={channelFilter}
            onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los canales</option>
            {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Pedido</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Canal</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Items</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                      No hay pedidos del Delivery Hub en esta sucursal
                    </td>
                  </tr>
                ) : orders.map((order) => {
                  const statusInfo = STATUS_LABELS[order.status] || { label: order.status, color: 'bg-slate-100 text-slate-600' };
                  const customer = order.customer_info || {};
                  const itemCount = (order.items || []).reduce((sum, i) => sum + (i.quantity || 1), 0);
                  const dateStr = order.created_at ? new Date(order.created_at).toLocaleString('es-MX', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                  }) : '--';

                  return (
                    <tr key={order.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-mono font-medium text-slate-900">
                          {order.external_order_id || order.id?.slice(0, 8)}
                        </div>
                        {order.is_overage && (
                          <span className="text-xs text-red-600 flex items-center gap-0.5 mt-0.5">
                            <AlertTriangle className="w-3 h-3" /> Excedente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {CHANNEL_LABELS[order.channel] || order.channel}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-900">{customer.name || 'Cliente'}</div>
                        {customer.phone && (
                          <div className="text-xs text-slate-400">{customer.phone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">
                        ${(order.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{dateStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {pagination.total} pedidos totales
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-slate-600">
                  {page} / {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DeliveryHub;
